import { type KeyboardEvent, type ReactNode } from 'react';

import './RadioCard.scss';

export type RadioCardVariant = 'inline' | 'stacked';

interface IRadioCardProps {
  selected: boolean;
  label?: ReactNode;
  tag?: ReactNode;
  children?: ReactNode;
  variant?: RadioCardVariant;
  title?: ReactNode;
  description?: ReactNode;
  tabbable?: boolean;
  onMove?: (direction: -1 | 1) => void;
  onSelect?: () => void;
}

const RadioCard = ({
  selected,
  label,
  tag,
  children,
  variant = 'inline',
  title,
  description,
  tabbable = true,
  onMove,
  onSelect
}: IRadioCardProps) => {
  const interactive = Boolean(onSelect);
  const stacked = variant === 'stacked';
  const className = ['radio-card', stacked ? 'radio-card--stacked' : '', selected ? 'radio-card--selected' : '']
    .filter(Boolean)
    .join(' ');

  const moveFocus = (event: KeyboardEvent<HTMLDivElement>, direction: -1 | 1) => {
    const group = event.currentTarget.parentElement;
    if (!group) return;
    const radios = Array.from(group.querySelectorAll<HTMLElement>('[role="radio"]:not([aria-disabled="true"])'));
    const from = radios.indexOf(event.currentTarget);
    if (from === -1 || radios.length === 0) return;
    radios[(from + direction + radios.length) % radios.length].focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onSelect) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onSelect();
      return;
    }
    if (!onMove) return;

    let direction: -1 | 1 | 0 = 0;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') direction = 1;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') direction = -1;
    if (direction === 0) return;

    event.preventDefault();
    onMove(direction);
    moveFocus(event, direction);
  };

  return (
    <div
      className={className}
      role="radio"
      aria-checked={selected}
      aria-disabled={interactive ? undefined : true}
      tabIndex={interactive && tabbable ? 0 : -1}
      onClick={onSelect}
      onKeyDown={interactive ? handleKeyDown : undefined}
    >
      <div className="radio-card__head">
        <span
          className={['radio-card__indicator', selected ? 'radio-card__indicator--selected' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {selected && <span className="radio-card__indicator-dot" />}
        </span>
        {stacked ? (
          <span className="radio-card__stack">
            <span className="radio-card__title">{title}</span>
            {description && <span className="radio-card__description">{description}</span>}
          </span>
        ) : (
          <>
            <span className="radio-card__label">{label}</span>
            {tag && <span className="radio-card__hint">{tag}</span>}
          </>
        )}
      </div>
      {children && !stacked && <div className="radio-card__body">{children}</div>}
    </div>
  );
};

export default RadioCard;
